import { NextStepActionResponse } from "pages/application/applicationService";
import { ActionButton, RemoveModal } from "..";
import "./finances.scss";
import { useGetBlocksQuery } from "../queries";
import { useGetAssetsQuery, useGetIncomeQuery } from "./queries";
import React, { useState, useEffect } from "react";
import { formatDollarAmount } from "utilities/string";
import useDesktopChecker from "hooks/useDesktopChecker";
import { trackOPTVoaCompleted, trackOPTVoiCompleted } from "utilities/optimizely";
import { GLOBAL_LINK_NO_TX_CLASS } from "utilities/globalLinkNoTx";

const Finances = () => {
  const { hasApplicant, hasSpouseCoApplicant, hasAssets, next } = useGetBlocksQuery({
    applicantBlockName: "financesApplicantIncome",
    spouseCoApplicantBlockName: "financesSpouseCoApplicantIncome",
  });

  return (
    <div className="finances-section">
      {hasApplicant && <ApplicantIncome />}
      {hasSpouseCoApplicant && <SpouseCoApplicantIncome />}
      {hasAssets && <Assets />}
      <ActionButton action="next" {...next} />
    </div>
  );
};

const ApplicantIncome = () => <Income blockType="financesApplicantIncome" />;
const SpouseCoApplicantIncome = () => (
  <Income blockType="financesSpouseCoApplicantIncome" />
);
export const Income = ({
  blockType,
}: {
  blockType: "financesApplicantIncome" | "financesSpouseCoApplicantIncome";
}) => {
  const { incomeLabel, name, electronicallyVerifiedIncomes, manuallyListedIncomes, addIncomeAction } =
    useGetIncomeQuery(blockType);
    const [showElectronicModal, setShowElectronicModal] = useState<boolean[]>([]);
    const [showManualModal, setShowManualModal] = useState<boolean[]>([]);
    
    const toggleElectronicModal = (index: number) => {
      setShowElectronicModal((prevState) => {
        const updatedState = [...prevState];
        updatedState[index] = !prevState[index];
        return updatedState;
      });
    };
    
    const toggleManualModal = (index: number) => {
      setShowManualModal((prevState) => {
        const updatedState = [...prevState];
        updatedState[index] = !prevState[index];
        return updatedState;
      });
    };

    useEffect(() => {
      if (electronicallyVerifiedIncomes.length > 0) {
        trackOPTVoiCompleted();
      }
    }, []);
    

  return (
    <div className="income section-border">
      {blockType === "financesApplicantIncome" && (
        <h3 className="section-subheader">{incomeLabel}</h3>
      )}
      <p className={`name ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="finances-applicant-name">{name}</p>
      {electronicallyVerifiedIncomes.length > 0 && (
        <div className="electronically-verified-incomes-details-wrapper">
            {electronicallyVerifiedIncomes.map((income, index) => (
              <React.Fragment key={income.source ?? index}>
                <IncomeDetails {...income} toggleModal={() => toggleElectronicModal(index)} />
                {showElectronicModal[index] && (
                  <RemoveModal
                    stepActionId={income.removeAction.stepActionId}
                    toggleModal={() => toggleElectronicModal(index)}
                    subContents={[income.source, `${formatDollarAmount(income.amount) ?? ""}`]}
                    label="Are you sure you want to remove income?"
                  />
                )}
              </React.Fragment>
            ))}
        </div>
      )}

      {manuallyListedIncomes.length > 0 && (
        <div className="manually-listed-incomes-details-wrapper">
            {manuallyListedIncomes.map((income, index) => (
              <React.Fragment key={income.source ?? index}>
                <IncomeDetails {...income} toggleModal={() => toggleManualModal(index)} />
                {showManualModal[index] && (
                  <RemoveModal
                    stepActionId={income.removeAction.stepActionId}
                    toggleModal={() => toggleManualModal(index)}
                    subContents={[income.source, `${formatDollarAmount(income.amount) ?? ""}`]}
                    label="Are you sure you want to remove income?"
                  />
                )}
              </React.Fragment>
            ))}
        </div>
      )}

      {electronicallyVerifiedIncomes.length === 0 && manuallyListedIncomes.length === 0 && (
        <div className="no-income">No Income Provided</div>
      )}

      {addIncomeAction && (
        <div className="add-income-container">
          <ActionButton action="add" {...addIncomeAction} />
        </div>
      )}
    </div>
  );
};


export const Assets = () => {
  const {
    assetsLabel,
    electronicallyVerifiedAssets,
    manuallyListedAssets,
    addAssetAction,
  } = useGetAssetsQuery();
  const [showModal, setShowModal] = useState<boolean[]>([]);
  const toggleModal = (index: number) => {
    setShowModal((prevState) => {
      const updatedState = [...prevState];
      updatedState[index] = !prevState[index];
      return updatedState;
    });
  };

  useEffect(() => {
    if (electronicallyVerifiedAssets.length > 0) {
      trackOPTVoaCompleted();
    }
  }, []);

  return (
    <div className="assets section-border">
      <h3 className="section-subheader">{assetsLabel}</h3>
      {electronicallyVerifiedAssets.length || manuallyListedAssets.length ? (
        <>
          {
            electronicallyVerifiedAssets.length > 0 && (
              <>
                <p className="category">{electronicallyVerifiedAssets[0]?.category}</p>
                <div className="electronically-verified-assets-details-wrapper">
                  {electronicallyVerifiedAssets.map((asset) => (
                    <AssetDetails key={asset.source} {...asset} showActions={false} />
                  ))}
                </div>
              </>)
          }
          {
            manuallyListedAssets.length > 0 && (
              <>
                <p className="category">{manuallyListedAssets[0]?.category}</p><div className="manually-listed-assets-details-wrapper">
                  {manuallyListedAssets.map((asset, index) => (
                    <React.Fragment key={asset.source ?? index}>
                      <AssetDetails
                        key={asset.source}
                        {...asset}
                        showActions
                        toggleModal={() => toggleModal(index)} />
                      {showModal[index] && (
                        <RemoveModal
                          stepActionId={asset.removeAction.stepActionId}
                          toggleModal={() => toggleModal(index)}
                          subContents={[
                            asset.source,
                            `${formatDollarAmount(asset.amount) ?? ""}`,
                          ]}
                          label="Are you sure you want to remove asset?" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )
          }
        </>
      ) : (
        <div className="no-assets">No Assets Provided</div>
      )}

      {addAssetAction && (
        <div className="add-assets-container">
          <ActionButton action="add" {...addAssetAction} />
        </div>
      )}
    </div>
  );
};

export const IncomeDetails = ({
  source,
  amount,
  editAction,
  removeAction,
  toggleModal,
}: {
  source: string;
  amount: string;
  editAction?: NextStepActionResponse;
  removeAction: NextStepActionResponse;
  toggleModal?: () => void;
}) => {
  const isDesktop = useDesktopChecker();
  return (
    <div className="income-details">
      {isDesktop ? (
        <div className="income-details__container">
          <div className={`income-details__source table-item ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="income-source">{source}</div>
          <div className="income-details__spacer"></div>
          <p className={`income-details__amount table-item ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="income-amount">
            {formatDollarAmount(amount)}
          </p>
          <div className="income-details__actions">
            <div className="income-details__actions__edit">{editAction && <ActionButton action="edit" {...editAction} />}</div>
            <div className="income-details__actions__remove">
              <ActionButton
                action="remove"
                {...removeAction}
                toggleModal={toggleModal}
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div>
            <div className={`income-details__source table-item ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="income-source">{source}</div>
            <p className={`income-details__amount table-item ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="income-amount">
              {formatDollarAmount(amount)}
            </p>
          </div>
          <div className="income-details__actions">
            {editAction && <ActionButton action="edit" {...editAction} />}
            <ActionButton
              action="remove"
              {...removeAction}
              toggleModal={toggleModal}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const AssetDetails = ({
  source,
  assetType,
  amount,
  editAction,
  removeAction,
  showActions,
  toggleModal,
}: {
  source: string;
  assetType: string;
  amount: string;
  editAction?: {
    label: string;
    stepActionId: string;
  };
  removeAction?: {
    label: string;
    stepActionId: string;
  };
  showActions: boolean;
  toggleModal?: () => void;
}) => {
  const isDesktop = useDesktopChecker();
  return (
    <div className="assets-details">
      {isDesktop ? (
        <div className="assets-details__container">
          <p className={`assets-details__source table-item ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="asset-source">{source}</p>
          <p className="assets-details__type table-item">{assetType}</p>
          <p className={`assets-details__amount table_item ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="asset-amount">
            {formatDollarAmount(amount)}
          </p>
          <div className="assets-details__actions">
            {showActions && <ActionButton action="edit" {...editAction!} />}
            {showActions && (
              <ActionButton
                action="remove"
                {...removeAction!}
                toggleModal={toggleModal}
              />
            )}
          </div>
        </div>
      ) : (
        <div>
          <div>
            <p className={`assets-details__source table-item ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="asset-source">{source}</p>
            <p className={`assets-details__amount table-item ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="asset-amount">
              {formatDollarAmount(amount)}
            </p>
          </div>
          <div>
            <p className="assets-details__type table-item">{assetType}</p>
          </div>
          {showActions && <ActionButton action="edit" {...editAction!} />}
          {showActions && (
            <ActionButton
              action="remove"
              {...removeAction!}
              toggleModal={toggleModal}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Finances;
